import {useSyncExternalStore, useCallback, useRef} from 'react';
import {i18n as _i18n, i18nInitialized, i18nReady} from '../i18n-instance';

type LocaleCallback<T> = (i18n: typeof _i18n.t, language: string) => T;

/**
 * React hook for localized strings that triggers re-renders when i18n initializes or language changes
 * @param cb Callback function that returns the localized strings
 * @returns Localized strings
 *
 * @example
 * const labels = useLocales((i18n) => ({
 *   DATE_INVALID: i18n('i18n.invalidDate'),
 *   REQUIRED: i18n('i18n.required'),
 * }));
 */
export const useLocales = <
	T extends Record<string | number, string> = Record<string | number, string>,
>(
	cb: LocaleCallback<T>,
): T => {
	const cbRef = useRef(cb);
	cbRef.current = cb;

	const subscribe = useCallback((onStoreChange: () => void) => {
		const handleLanguageChanged = () => {
			onStoreChange();
		};

		const handleLoaded = () => {
			onStoreChange();
		};

		// Subscribe to language changes
		_i18n.on('languageChanged', handleLanguageChanged);
		_i18n.on('loaded', handleLoaded);

		// If not ready yet, also trigger when initialized
		if (!i18nReady) {
			i18nInitialized.then(onStoreChange);
		}

		return () => {
			_i18n.off('languageChanged', handleLanguageChanged);
			_i18n.off('loaded', handleLoaded);
		};
	}, []);

	const getSnapshot = useCallback(() => {
		// Return the language as the snapshot - when it changes, React knows to re-render
		return _i18n.language;
	}, []);

	const getServerSnapshot = useCallback(() => {
		return 'en'; // Default for SSR
	}, []);

	// This will cause a re-render when the language snapshot changes
	useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	// Return fresh translations on each render
	return cb(_i18n.t.bind(_i18n), _i18n.language);
};

export default useLocales;
